import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@core/components/ui/dialog";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { Label } from "@core/components/ui/label";
import { Textarea } from "@core/components/ui/textarea";
import { useAuth } from "@core/contexts/AuthContext";
import { useToast } from "@core/hooks/use-toast";
import { SupportTicketService } from "@core/services/SupportTicketService";

/**
 * « Signaler ce sondage » (spec « utilisable par les IA », §3.7, UC12, R1).
 *
 * Présent sur la page de vote de TOUS les sondages. Crée un ticket
 * `support_tickets` de type `abuse` avec le `poll_id` ; la limite de débit
 * des tickets (trigger `enforce_support_tickets_rate_limit`) s'applique.
 */

export const REPORT_REASONS = [
  "spam",
  "phishing",
  "inappropriate",
  "other",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_DETAILS_MAX = 500;

/**
 * La colonne `email` est NOT NULL et la policy INSERT exige un format
 * d'e-mail. Un invité qui ne laisse pas d'adresse signale avec cette adresse
 * technique (TLD `.invalid`, réservé : jamais délivrable). Conséquence voulue :
 * la limite anon « 3 / e-mail / 10 min » devient un plafond global pour les
 * signalements anonymes sans adresse — un frein au signalement en masse.
 */
export const ANONYMOUS_REPORT_EMAIL = "abuse-report@doodates.invalid";

/** Même motif que la policy `Users insert own tickets`. */
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const SEVERITY_BY_REASON: Record<ReportReason, string> = {
  spam: "normal",
  phishing: "high",
  inappropriate: "normal",
  other: "low",
};

type Props = {
  pollId: string;
  pollSlug?: string;
  pollTitle?: string;
};

export function ReportPollLink({ pollId, pollSlug, pollTitle }: Props) {
  const { t } = useTranslation("polls");
  const { user } = useAuth();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | "">("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const openDialog = () => {
    setReason("");
    setDetails("");
    setEmail(user?.email ?? "");
    setError(null);
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) {
      setError(t("report_poll.error_reason_required"));
      return;
    }
    if (details.length > REPORT_DETAILS_MAX) {
      setError(
        t("report_poll.error_details_too_long", { max: REPORT_DETAILS_MAX }),
      );
      return;
    }
    const contact = email.trim();
    if (contact && !EMAIL_RE.test(contact)) {
      setError(t("report_poll.error_email_invalid"));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await SupportTicketService.getInstance().createTicket({
        email: contact || user?.email || ANONYMOUS_REPORT_EMAIL,
        subject: `[ABUSE] ${reason} — ${(pollTitle ?? pollSlug ?? pollId).slice(0, 80)}`,
        content: details.trim(),
        type: "abuse",
        severity: SEVERITY_BY_REASON[reason],
        priority: reason === "phishing" ? 2 : 1,
        pollId,
        asCurrentUser: !!user,
        metadata: {
          source: "report_poll_button",
          reason,
          details: details.trim() || null,
          poll_slug: pollSlug ?? null,
          reporter_logged_in: !!user,
          reporter_contact_provided: !!contact,
          page_url:
            typeof window !== "undefined" ? window.location.pathname : null,
        },
      });
      setOpen(false);
      toast({
        title: t("report_poll.success_title"),
        description: t("report_poll.success_description"),
      });
    } catch (err) {
      const rateLimited =
        err instanceof Error && err.message.includes("rate_limit_exceeded");
      toast({
        title: t("report_poll.error_title"),
        description: rateLimited
          ? t("report_poll.error_rate_limited")
          : t("report_poll.error_generic"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex justify-center px-4 py-3">
        <button
          type="button"
          onClick={openDialog}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          data-testid="report-poll-link"
        >
          <Flag className="h-3 w-3" aria-hidden="true" />
          {t("report_poll.link")}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto rounded-lg sm:w-full"
          data-testid="report-poll-dialog"
        >
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <DialogHeader>
              <DialogTitle className="pr-8">
                {t("report_poll.title")}
              </DialogTitle>
              <DialogDescription>
                {t("report_poll.description")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5">
              <Label htmlFor="report-reason">
                {t("report_poll.reason_label")}
              </Label>
              <select
                id="report-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value as ReportReason | "")}
                className="w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                data-testid="report-reason"
              >
                <option value="">{t("report_poll.reason_placeholder")}</option>
                {REPORT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`report_poll.reasons.${r}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="report-details">
                {t("report_poll.details_label")}
              </Label>
              <Textarea
                id="report-details"
                value={details}
                onChange={(e) =>
                  setDetails(e.target.value.slice(0, REPORT_DETAILS_MAX))
                }
                maxLength={REPORT_DETAILS_MAX}
                rows={3}
                className="resize-y break-words"
                data-testid="report-details"
              />
              <p className="text-right text-xs text-muted-foreground">
                {details.length}/{REPORT_DETAILS_MAX}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="report-email">
                {t("report_poll.email_label")}
              </Label>
              <Input
                id="report-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("report_poll.email_placeholder")}
                data-testid="report-email"
              />
              <p className="text-xs text-muted-foreground">
                {t("report_poll.email_hint")}
              </p>
            </div>

            {error && (
              <p
                role="alert"
                className="text-sm text-destructive"
                data-testid="report-error"
              >
                {error}
              </p>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                {t("report_poll.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="report-submit"
              >
                {submitting
                  ? t("report_poll.submitting")
                  : t("report_poll.submit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
